package dev.lingvo.ops.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.dom.svg.Path
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.dom.svg.ViewBox
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.AuthProvidersDto
import dev.lingvo.ops.state.rememberOpsResource
import dev.lingvo.site.api.API_JSON
import dev.lingvo.site.api.ApiConfig
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LLinkButton
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.decodeFromString
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

/** Ops has stricter readiness than product sign-in, so it does not reuse the product's cache. */
@Composable
fun OpsGoogleSignIn() {
    // Only the login page loads this. A callback must clear its code before any network request.
    val resource = rememberOpsResource(Unit) {
        val response = window.fetch(
            "${ApiConfig.baseUrl}/auth/providers?surface=ops",
            RequestInit(method = "GET", headers = Headers().apply { append("Accept", "application/json") }),
        ).await()
        check(response.ok)
        API_JSON.decodeFromString<AuthProvidersDto>(response.text().await()).google
    }
    when (val availability = resource.state.load) {
        OpsLoad.Loading -> Div(Modifier.height(40.px).toAttrs { attr("aria-hidden", "true") })
        is OpsLoad.Failed, is OpsLoad.OfflineStale -> {
            LAlert("Google sign-in unavailable", "Could not check sign-in options. Use your password or retry.",
                variant = LAlertVariant.WARNING, live = true)
            LButton("Retry sign-in options", resource::refresh, variant = LButtonVariant.SECONDARY)
        }
        is OpsLoad.Ready -> if (availability.value) LLinkButton(
            label = "Continue with Google",
            path = "${ApiConfig.baseUrl}/auth/google/start?surface=ops",
            modifier = Modifier.fillMaxWidth().attr("data-ops-google-sign-in", ""),
            variant = LButtonVariant.SECONDARY,
            leadingIcon = { GoogleGlyph() },
        )
    }
}

/** Google's four-colour brand mark; no external script, font or embedded sign-in widget. */
@Composable
private fun GoogleGlyph() {
    Svg(attrs = Modifier.width(18.px).height(18.px).toAttrs {
        viewBox(ViewBox.sized(48, 48))
        attr("aria-hidden", "true")
        attr("focusable", "false")
    }) {
        listOf(
            "#EA4335" to "M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z",
            "#4285F4" to "M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z",
            "#FBBC05" to "M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z",
            "#34A853" to "M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z",
        ).forEach { (colour, path) -> Path { d(path); attr("fill", colour) } }
    }
}
